<template>
  <div>
    <Teaser
      v-if="isElementVisible"
      v-model="teaserDimensions"
      :teaser-text="teaserStore.teaser.message"
      :quickreplies="teaserStore.teaser?.qr"
      :is-mobile-teaser="isMobileTeaser"
      :alignment-origin="alignmentOrigin.x"
      @on-close="handleClosebutton"
      @on-intent="handleQuickReplyClick"
    />
  </div>
</template>

<script setup lang="ts">
import Teaser from "./Teaser.vue";
import { storeToRefs } from "pinia";
import { computed, onBeforeMount, ref } from "vue";
import { useLayoutStore } from "@/store/layout";
import { useI18n } from "@/composables/useI18n.js";
import { useIframeManager } from "@/composables/useIframeManager";
import { useTeaserStore } from "@/store/teaser";
import type { Intent } from "@/types";

const props = defineProps<{
  frame: HTMLIFrameElement;
}>();

const teaserDimensions = ref({
  width: "400px",
  height: "100vh",
});

const layoutStore = useLayoutStore();
const teaserStore = useTeaserStore();
const { t } = useI18n();

const {
  teaserOffset: offsetOverwrites,
  alignmentOrigin,
  zIndex,
  isMobileTeaser,
} = storeToRefs(layoutStore);
const isElementVisible = computed(() => teaserStore.showTeaser);

// ##############################
// Teaser defaults:
// 0px on the x axis and 0px on the y axis mobile
// 20px on the x axis and 20px on the x axis on desktop
// on mobile we omit position changes on the x axis and only allow changes on the y axis
const xOffset = computed(() => {
  // defaults
  let value = isMobileTeaser.value ? "0px" : "20px";
  // overwrites via config / api if available
  if (offsetOverwrites.value?.x) {
    value = offsetOverwrites.value.x;
  }
  // on we omit position changes on the x axis and only allow changes on the y axis
  return isMobileTeaser.value ? "0px" : value;
});

const yOffset = computed(() => {
  // default behavior for mobile teaser and desktop teaser
  let value = isMobileTeaser.value ? "0px" : "20px";
  // if we have a value for the y axis set via api or config, we use that instead
  if (offsetOverwrites.value?.y) {
    value = offsetOverwrites.value.y;
  }
  // but we ignore sone axis on mobile
  return value;
});

const teaserLayout = computed(() => {
  return {
    name: "teaser",
    isElementVisible: isElementVisible.value,
    width: isMobileTeaser.value ? "100%!important" : teaserDimensions.value.width,
    height: teaserDimensions.value.height,
    // on we omit position changes on the x axis and only allow changes on the y axis
    x: xOffset.value,
    y: yOffset.value,
    xAlignment: alignmentOrigin.value.x,
    yAlignment: alignmentOrigin.value.y,
    zIndex: zIndex.value,
  };
});

const attrs = computed(() => {
  return {
    "aria-hidden": !isElementVisible.value,
    title: t("iframe.teaser.title"),
    name: "moin-ai-teaser",
  };
});

useIframeManager(props.frame, teaserLayout, undefined, attrs);

onBeforeMount(() => {
  Object.assign(props.frame.style, {
    width: "500px",
    height: "100vh",
    // outline: '1px solid red',
  });
});

const handleClosebutton = () => {
  teaserStore.handleCloseTeaserClick();
};

const handleQuickReplyClick = (intent: Intent) => {
  teaserStore.handleQuickReplyClick(intent);
};
</script>

<style lang="scss">
@media print {
  #moinai-container {
    display: none;
  }
}
</style>
